<template>
    <div class="block-layout-columns">
        <div class="block-layout-columns__inner">
            <div class="block-layout-columns__column">
                <transition name="fade-in">
                    <div 
                        v-if="!isWin || (isWin && data && data.name)"
                        class="block-auth-login-magic-with-teaser__content"
                    >
                        <AtomTextHeading
                            v-if="title"
                            class="block-auth-login-magic-with-teaser__title"
                            :text="title"
                            font-size="h1"
                            html-tag="h1"
                            :replace-values="data?.name ? { prizeName: data.name } : {}"
                        />
        
                        <AtomTextRichtext
                            v-if="text"
                            class="block-auth-login-magic-with-teaser__text"
                            :html="useAlternateText ? alternateText : text"
                        />

                        <div v-if="additionalLink" 
                            class="block-auth-login-magic-with-teaser__additional-link"
                        >
                            <AtomInputLink
                                :link="additionalLink.link"
                                :link-text="additionalLink.text"
                                :target="additionalLink.target"
                            />
                        </div>
                        
                        <div 
                            v-if="isArrayNotEmpty(buttons)"
                            class="block-auth-login-magic-with-teaser__buttons">
                            
                            <component :is="button.link ? UtilRouteLink : 'div'"
                                v-for="(button, index) in computedButtons"
                                :key="`button-${index}`"
                                :link="button.link ? button.link : null"
                                class="block-auth-login-magic-with-teaser__button"
                                :target="button.target"
                                :class="{
                                    'is-hidden': button.isFlipCard && isFlipped
                                }"
                            >
                                <AtomButton
                                    :text="button.text"
                                    :link="button.link ? button.link : null"
                                    :callback="button.isFlipCard ? flipCard : null"
                                />
                            </component>
                        </div>
                    </div>
                </transition>
            </div>
            
            <div class="block-layout-columns__column">
                <transition name="fade-in">
                    <div
                        v-if="image && image.src"
                        class="block-auth-login-magic-with-teaser__card"
                        :class="{ 'is-flipped': isFlipped }"
                    >
                        <div class="block-auth-login-magic-with-teaser__card-inner">
                            <BlockAuthLoginMagic
                                class="block-auth-login-magic-with-teaser__card-login-form"
                                v-bind="loginForm"
                            />
                            
                            <div class="block-auth-login-magic-with-teaser__card-image">
                                <AtomImage
                                    class="block-auth-login-magic-with-teaser__card-image-media"
                                    behaviour="contain"
                                    :image="image" 
                                />

                                <AtomButton
                                    v-if="!isAuthenticated"
                                    class="block-auth-login-magic-with-teaser__card-image-button"
                                    textColor="var(--c-primary)"
                                    backgroundColor="var(--c-white)"
                                    borderColor="var(--c-white)"
                                    :text="isAuthenticated ? imageButtonTextAuthenticated : imageButtonText"
                                    :callback="flipCard"
                                />
                            </div>
                        </div>
                    </div>
                </transition>
            </div>
        </div>
    </div>
</template>

<script setup>
import UtilRouteLink from '~/components/Util/Route/UtilRouteLink.vue';

const props = defineProps({
    title: {
        type: String,
        default: '',
    },
    
    text: {
        type: String,
        default: '',
    },

    alternateText: {
        type: String,
        default: '',
    },

    image: {
        type: Object,
        default: () => {},
    },

    buttons: {
        type: Array,
        default: () => [],
    },

    imageButtonText: {
        type: String,
        default: '',
    },

    imageButtonTextAuthenticated: {
        type: String,
        default: '',
    },

    additionalLink: {
        type: Object,
        default: () => {},
    },

    loginForm: {
        type: Object,
        default: () => {},
    },

    isWin: {
        type: Boolean,
        default: false,
    },

    loggedInRedirect: {
        type: String,
        default: '',
    },
});

const {query} = useRoute();
const useAlternateText = query.use_alternate_text === 'true';

const computedButtons = computed(() => {
    return props.buttons.filter((button) => {
        if(button.isAuthenticated === undefined) return true;

        return button.isAuthenticated === isAuthenticated.value;
    });
});

const isFlipped = ref(false);
const flipCard = () => {
    if(isAuthenticated.value) {
        navigateTo(props.loggedInRedirect || '/');
        return;
    }

    isFlipped.value = !isFlipped.value;
};

/* 
    get price
*/
const receiptDocumentIdCookie = useCookie('receiptDocumentId');
const image = ref(null);
const localePath = useLocalePath();
const { locale } = useI18n();

if(props.isWin && !receiptDocumentIdCookie.value) {
    navigateTo('/');
}

if(!props.isWin) {
    image.value = props.image;
}

const data = ref(null);
const error = ref(null);
const status = ref('idle');

const execute = async () => {
    status.value = 'pending';
    error.value = null;

    try {
        const response = await useDirectApiFetch('/api/receipts/result', {
            method: 'POST',
            body: {
                receipt_document_id: receiptDocumentIdCookie.value,
                locale: locale.value
            },
        });

        data.value = response?.prize_characteristics ?? null;
        status.value = 'success';
        return data.value;
    } catch (e) {
        error.value = e;
        data.value = null;
        status.value = 'error';
        return null;
    }
};

watch(() => error.value, (e) => {
    if (e) {
        useSentryError(e, {
            useThrow: false,
            response: data.value,
        });
    }
});

watchEffect(() => {
    if(data.value && data.value?.image?.url) {
        image.value = {
            src: data.value.image.url,
            alt: data.value.image.alt,
        };
    }
});

watch(() => status.value, (newValue) => {
    if(newValue === 'success' && !data.value) {
        navigateTo(localePath('/'));
    }
})

if(receiptDocumentIdCookie.value && props.isWin) {
    execute();
}
</script>

<style lang="scss" scoped>
.block-layout-columns {
    @include wrapper-layout;
    height: auto;
    min-height: 0;
    margin-bottom: var(--m-bottom--big);
}

.block-layout-columns__inner {
    @include grid-layout(1, 14, 14);
    align-items: center;
    row-gap: 60px;

    @include tablet {
        row-gap: var(--grid-row--gap);
    }
}

.block-layout-columns__column {
    display: flex;
    flex-direction: column;
    justify-content: center;
    grid-column-end: span 1;
    row-gap: var(--grid-row--gap);

    &:first-child {
        @include tablet {

            grid-column-end: span 7;
            grid-column-start: 1;
        }
    }

    &:last-child {
        @include tablet {
            grid-column-end: span 6;
            grid-column-start: 9;
        }
    }
}

.block-auth-login-magic-with-teaser__card {
    position: relative;
    display: grid;
    border-radius: var(--b-radius--box);
    // background-color: var(--c-card--bg);
    perspective: 1200px;

    :deep(.block-auth-login-magic__inner) {
        display: flex;
        height: 100%;
        flex-direction: column;
        justify-content: center;
    }
}

.block-auth-login-magic-with-teaser__card::before {
    padding-top: 100%;
    content: '';
    grid-area: 1 / 1;
}

.block-auth-login-magic-with-teaser__content {
    display: flex;
    flex-direction: column;
    row-gap: var(--grid-row--gap);
}

.block-auth-login-magic-with-teaser__card-inner {
    position: relative;
    width: 100%;
    min-height: 100%;
    grid-area: 1 / 1;
    transform-style: preserve-3d;
    transition: transform 0.6s ease;
}

.block-auth-login-magic-with-teaser__card.is-flipped .block-auth-login-magic-with-teaser__card-inner {
    transform: rotateY(180deg);
}

.block-auth-login-magic-with-teaser__card-login-form {
    position: relative;
    z-index: 1;
    width: 100%;
    min-height: 100%;
    padding: unset;
    backface-visibility: hidden;
    grid-area: 1 / 1;
    transform: rotateY(180deg);
}

.block-auth-login-magic-with-teaser__additional-link {
    margin-bottom: 20px;
}

.block-auth-login-magic-with-teaser__card-image {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    display: flex;
    overflow: hidden;
    align-items: center;
    justify-content: center;
    border-radius: var(--b-radius--box);
    backface-visibility: hidden;
    background: var(--c-box--bg);
}

.block-auth-login-magic-with-teaser__card-image-media {
    width: 100%;
    height: 100%;
}

.block-auth-login-magic-with-teaser__card-image-media :deep(.atom-image__wrapper),
.block-auth-login-magic-with-teaser__card-image-media :deep(.atom-image__background),
.block-auth-login-magic-with-teaser__card-image-media :deep(img),
.block-auth-login-magic-with-teaser__card-image-media :deep(.atom-image) {
    width: 100%;
    height: 100%;
}

.block-auth-login-magic-with-teaser__card:not(.is-flipped) .block-auth-login-magic-with-teaser__card-login-form {
    pointer-events: none;
}

.block-auth-login-magic-with-teaser__card.is-flipped .block-auth-login-magic-with-teaser__card-image {
    pointer-events: none;
}

.block-auth-login-magic-with-teaser__card-image-button {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
}

.block-auth-login-magic-with-teaser__buttons {
    display: flex;
    flex-wrap: wrap;
    column-gap: var(--grid-gutter);
    row-gap: var(--grid-row--gap);
}

.block-auth-login-magic-with-teaser__button {
    &.is-hidden {
        opacity: 0;
        pointer-events: none;
    }
}
</style>